// frontend/src/pages/TagFeed.js

import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "api";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { X } from "phosphor-react";

import AppHeader from "../components/AppHeader";
import ProfileCard from "../components/ProfileCard";
import ActivityCard from "../components/ActivityCard";
import ActivityUpload from "./ActivityUpload";
import CirclesPanel from "../components/CirclesPanel";
import ConnectionsCard from "../components/ConnectionsCard";
import PlannerQuickPanel from "../components/PlannerQuickPanel";

import MonthlyCalendarModal from "../components/MonthlyCalendarModal";
import WeeklyCalendarModal from "../components/WeeklyCalendarModal";
import WeeklyPlanModal from "../components/WeeklyPlanModal";
import GoalsByMonth from "../components/GoalsByMonth";
import RemindersModal from "../components/RemindersModal";

import { isIOS } from "../utils/platform";
import { toAbsolute } from "../utils/url";
import { DEFAULT_CIRCLE_IMAGE } from "../utils/blank-image";
import { computeCalendarMarks } from "../utils/streaks";

/* ---------- Auth ---------- */

const getToken = async () => {
  try {
    const pref = await Preferences.get({ key: "auth_token" });
    if (pref?.value) return pref.value;
  } catch {}

  if (typeof window !== "undefined") {
    return (
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      null
    );
  }

  return null;
};

const getAuthHeaders = async () => {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/* ---------- Responsive / native check ---------- */

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.innerWidth < 768
  );

  useEffect(() => {
    const onResize = () =>
      setIsMobile(window.innerWidth < 768);

    window.addEventListener("resize", onResize);

    return () =>
      window.removeEventListener("resize", onResize);
  }, []);

  const nativeOrIOS =
    typeof navigator !== "undefined" &&
    (/iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (typeof Capacitor?.isNativePlatform === "function" &&
        Capacitor.isNativePlatform()));

  return isMobile || nativeOrIOS;
}

/* ---------- Planner helpers ---------- */

// date -> "YYYY-MM-DD"
const ymd = (d) => {
  const dt = new Date(d);

  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");

  return `${y}-${m}-${day}`;
};

const groupByDay = (list = []) => {
  const map = {};

  list.forEach((a) => {
    const key = ymd(a.timestamp || a.created_at);
    (map[key] ||= []).push(a);
  });

  Object.keys(map).forEach((k) => {
    map[k].sort(
      (a, b) =>
        new Date(a.timestamp || a.created_at) -
        new Date(b.timestamp || b.created_at)
    );
  });

  return map;
};

/* ---------- Tag helper ---------- */

// very light stemmer for "...ing" companion search
const gerundPair = (t) => {
  const tag = (t || "").toLowerCase();

  if (!tag) {
    return {
      primary: tag,
      companion: null,
    };
  }

  if (tag.endsWith("ing")) {
    const base = tag.slice(0, -3);

    return {
      primary: tag,
      companion: base || null,
    };
  }

  return {
    primary: tag,
    companion: `${tag}ing`,
  };
};

export default function TagFeed() {
  const { tag: rawTag } = useParams();

  const tag = useMemo(
    () => String(rawTag || "").toLowerCase(),
    [rawTag]
  );

  const isMobile = useIsMobile();
  const onIOS = !!isIOS;

  /* ---------- Main data ---------- */

  const [user, setUser] = useState(null);
  const [activities, setActivities] = useState([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  /* ---------- Home-style rails ---------- */

  const [joinedCircles, setJoinedCircles] = useState([]);
  const [myActivities, setMyActivities] = useState([]);

  const leftRailRef = React.useRef(null);
  const rightRailRef = React.useRef(null);

  const [desktopRailHeight, setDesktopRailHeight] =
    useState(null);

  /* ---------- Planner / upload ---------- */

  const [uploadOpen, setUploadOpen] = useState(false);

  const [showMonthly, setShowMonthly] = useState(false);
  const [showWeekly, setShowWeekly] = useState(false);
  const [showWeekPlan, setShowWeekPlan] = useState(false);
  const [showReminders, setShowReminders] =
    useState(false);
  const [showGoalsByMonth, setShowGoalsByMonth] =
    useState(false);

  /* ---------- Tag feed loader ---------- */

  const fetchTaggedActivities = async (
    headers = null
  ) => {
    const authHeaders =
      headers || (await getAuthHeaders());

    const { primary, companion } =
      gerundPair(tag);

    const fetchFeed = async (t) => {
      if (!t) return [];

      try {
        try {
          const r = await api.get("/api/feed", {
            params: { tag: t },
            headers: authHeaders,
          });

          return Array.isArray(r.data)
            ? r.data
            : [];
        } catch {
          const r = await api.get("/feed", {
            params: { tag: t },
            headers: authHeaders,
          });

          return Array.isArray(r.data)
            ? r.data
            : [];
        }
      } catch {
        return [];
      }
    };

    const [a1, a2] = await Promise.all([
      fetchFeed(primary),
      fetchFeed(companion),
    ]);

    // de-dupe by id and sort newest first
    const byId = new Map();

    [...a1, ...a2].forEach((a) => {
      if (a?.id) {
        byId.set(a.id, a);
      }
    });

    return Array.from(byId.values()).sort(
      (a, b) => {
        const ta = new Date(
          a.timestamp ||
            a.created_at ||
            0
        ).getTime();

        const tb = new Date(
          b.timestamp ||
            b.created_at ||
            0
        ).getTime();

        return tb - ta;
      }
    );
  };

  /* ---------- Initial / tag loading ---------- */

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setErr("");

      try {
        const headers = await getAuthHeaders();

        /*
         * Current user.
         * This is also used by the Home-style rails.
         */
        let me = null;

        try {
          const meRes = await api.get(
            "/users/me",
            { headers }
          );

          me = meRes.data || null;

          if (!cancelled) {
            setUser(me);
          }
        } catch {
          if (!cancelled) {
            setUser(null);
          }
        }

        /*
         * Load the rail data separately so a rail request
         * cannot interfere with the tag results.
         */
        if (me?.id) {
          try {
            const [circlesRes, mineRes] =
              await Promise.all([
                api.get(
                  `/users/${me.id}/groups`,
                  { headers }
                ),
                api.get(
                  `/users/${me.id}/activities`,
                  { headers }
                ),
              ]);

            if (!cancelled) {
              const circlesData =
                Array.isArray(circlesRes.data)
                  ? circlesRes.data
                  : circlesRes.data?.items || [];

              setJoinedCircles(
                circlesData.map((circle) => ({
                  ...circle,

                  image_url:
                    toAbsolute(
                      circle?.image_url ||
                        circle?.icon_url ||
                        circle?.avatar_url
                    ) ||
                    DEFAULT_CIRCLE_IMAGE,
                }))
              );

              setMyActivities(
                Array.isArray(mineRes.data)
                  ? mineRes.data
                  : []
              );
            }
          } catch (railError) {
            console.error(
              "Failed to load TagFeed rail data:",
              railError
            );

            if (!cancelled) {
              setJoinedCircles([]);
              setMyActivities([]);
            }
          }
        }

        /*
         * Keep the existing TagFeed result behavior:
         * primary + gerund companion,
         * de-duped by ID,
         * newest first.
         */
        const merged =
          await fetchTaggedActivities(headers);

        if (cancelled) return;

        setActivities(merged);
      } catch (e) {
        if (!cancelled) {
          setErr(
            e?.response?.data?.detail ||
              e?.message ||
              "Failed to load tag feed."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag]);

  /* ---------- Match middle height to taller rail ---------- */

  useEffect(() => {
    if (!user) return;

    const updateDesktopRailHeight = () => {
      if (
        onIOS ||
        isMobile ||
        window.innerWidth < 768
      ) {
        setDesktopRailHeight(null);
        return;
      }

      const leftHeight =
        leftRailRef.current
          ?.getBoundingClientRect()
          .height || 0;

      const rightHeight =
        rightRailRef.current
          ?.getBoundingClientRect()
          .height || 0;

      const maxRailHeight = Math.ceil(
        Math.max(
          leftHeight,
          rightHeight
        )
      );

      if (maxRailHeight > 0) {
        setDesktopRailHeight(
          maxRailHeight
        );
      }
    };

    const frame = requestAnimationFrame(
      updateDesktopRailHeight
    );

    const observer =
      new ResizeObserver(
        updateDesktopRailHeight
      );

    if (leftRailRef.current) {
      observer.observe(
        leftRailRef.current
      );
    }

    if (rightRailRef.current) {
      observer.observe(
        rightRailRef.current
      );
    }

    window.addEventListener(
      "resize",
      updateDesktopRailHeight
    );

    return () => {
      cancelAnimationFrame(frame);

      observer.disconnect();

      window.removeEventListener(
        "resize",
        updateDesktopRailHeight
      );
    };
  }, [
    user,
    isMobile,
    onIOS,
  ]);

  /* ---------- Planner derived data ---------- */

  const myActivitiesByDay =
    useMemo(
      () =>
        groupByDay(
          myActivities
        ),
      [myActivities]
    );

  const streakDays =
    useMemo(
      () =>
        computeCalendarMarks({
          activityDates: Object.keys(
            myActivitiesByDay || {}
          ),
        }).streakCount,
      [myActivitiesByDay]
    );

  /* ---------- Refresh after upload ---------- */

  const handleUploadSuccess =
    async () => {
      try {
        const headers =
          await getAuthHeaders();

        if (user?.id) {
          try {
            const mineRes =
              await api.get(
                `/users/${user.id}/activities`,
                { headers }
              );

            setMyActivities(
              Array.isArray(mineRes.data)
                ? mineRes.data
                : []
            );
          } catch (e) {
            console.error(
              "Failed to refresh my activities:",
              e
            );
          }
        }

        const freshTagActivities =
          await fetchTaggedActivities(
            headers
          );

        setActivities(
          freshTagActivities
        );
      } catch (e) {
        console.error(
          "Failed to refresh tag feed:",
          e
        );
      }
    };

  const container =
    "max-w-7xl mx-auto px-6 md:px-10 xl:px-16";

  return (
    <div className="app-shell bg-blue-50 min-h-screen text-[#181411]">
      <header className="app-header">
        <AppHeader
          user={user || undefined}
        />
      </header>

      <main
        className={`
          app-content
          w-full
          ${container}
          pt-[calc(64px+var(--safe-top))]
          md:mt-4
          px-0
          py-8
        `}
      >
        <div
          className="
            grid
            grid-cols-1
            md:grid-cols-[1fr_minmax(0,2fr)_1fr]
            gap-10
            md:overflow-visible
          "
          style={{
            height: "auto",
          }}
        >
          {/* LEFT — same rail structure as Home */}
          <div className="-mb-8 md:self-start">
            <div
              ref={leftRailRef}
              className="md:sticky top-0 md:h-max"
              style={{
                height: "max-content",
              }}
            >
              <ProfileCard
                user={user}
                viewer={user}
                isSelf
              />

              {!(onIOS || isMobile) && (
                <div className="hidden md:block mt-4">
                  <PlannerQuickPanel
                    keySuffix="tagfeed"
                    monthLabel={new Date()
                      .toLocaleString(
                        "en-US",
                        {
                          month: "short",
                        }
                      )
                      .toUpperCase()}
                    onMonthly={() =>
                      setShowMonthly(true)
                    }
                    onWeekly={() =>
                      setShowWeekly(true)
                    }
                    onWeekPlan={() =>
                      setShowWeekPlan(true)
                    }
                    onGoals={() =>
                      setShowGoalsByMonth(
                        true
                      )
                    }
                    onReminders={() =>
                      setShowReminders(
                        true
                      )
                    }
                    streakDays={
                      streakDays
                    }
                    activitiesByDay={
                      myActivitiesByDay
                    }
                  />
                </div>
              )}
            </div>
          </div>

          {/* MIDDLE */}
          <section
            className={`
              md:col-span-1
              flex
              flex-col
              min-h-0
              space-y-4
              ${
                onIOS || isMobile
                  ? "overflow-visible"
                  : "overflow-hidden"
              }
            `}
            style={
              onIOS || isMobile
                ? undefined
                : {
                    height:
                      desktopRailHeight !=
                      null
                        ? `${desktopRailHeight}px`
                        : undefined,
                  }
            }
          >
            {/*
              FIXED / NON-SCROLLING HEADER

              On desktop this stays outside the
              overflow-y-auto activity area below.
            */}
            <div className="shrink-0 mt-2 sm:mt-0 px-5">
              <p className="text-sm font-medium text-gray-600">
                Activities tagged with{" "}
                <span className="font-semibold text-blue-700">
                  #{tag}
                </span>
              </p>
            </div>

            {/*
              ONLY THIS PORTION SCROLLS ON DESKTOP.

              The title above does not move.
              On mobile this returns to normal page scrolling,
              matching Home's mobile behavior.
            */}
            <div
              className={`
                relative
                z-0
                min-h-0
                flex-1
                space-y-4
                ${
                  onIOS || isMobile
                    ? "overflow-visible"
                    : "overflow-y-auto overscroll-y-contain no-scrollbar pb-28"
                }
              `}
            >
              {loading && (
                <div className="px-6">
                  Loading…
                </div>
              )}

              {err && !loading && (
                <div className="px-6 text-red-600">
                  {err}
                </div>
              )}

              {!loading &&
                !err &&
                activities.length ===
                  0 && (
                  <div className="px-5 text-sm text-gray-500">
                    No activities yet
                    for #{tag}
                  </div>
                )}

              {activities.map(
                (activity) => (
                  <ActivityCard
                    key={
                      activity.id
                    }
                    activity={
                      activity
                    }
                    currentUser={
                      user ||
                      undefined
                    }
                    onTagClick={(
                      t
                    ) => {
                      if (
                        typeof window !==
                        "undefined"
                      ) {
                        window.location.assign(
                          `/tags/${encodeURIComponent(
                            t
                          )}`
                        );
                      }
                    }}
                  />
                )
              )}
            </div>
          </section>

          {/* RIGHT — same desktop rail as Home */}
          {!(onIOS || isMobile) && (
            <aside className="hidden md:block self-stretch">
              <div
                ref={rightRailRef}
                className="sticky md:z-0"
                style={{
                  top: "calc(env(safe-area-inset-top) + 0px)",
                  height:
                    "max-content",
                }}
              >
                {/* Upload Activity */}
                <div className="mb-4 pointer-events-auto">
                  <button
                    type="button"
                    onClick={() =>
                      setUploadOpen(
                        true
                      )
                    }
                    className="
                      w-full
                      rounded-full
                      bg-blue-600
                      text-[16px]
                      px-4
                      py-2
                      font-semibold
                      text-white
                      transition
                      hover:bg-blue-700
                    "
                  >
                    Upload Activity
                  </button>
                </div>

                <CirclesPanel
                  joinedCircles={
                    joinedCircles
                  }
                  self
                  ownerName={(
                    user?.display_name ||
                    user?.name ||
                    [
                      user?.first_name,
                      user?.last_name,
                    ]
                      .filter(Boolean)
                      .join(" ") ||
                    user?.username ||
                    (user?.id
                      ? `User #${user.id}`
                      : "You")
                  ).trim()}
                />

                <div className="mt-4">
                  <ConnectionsCard
                    meId={user?.id}
                    viewerId={
                      user?.id
                    }
                    ownerName={(
                      user?.display_name ||
                      user?.name ||
                      [
                        user?.first_name,
                        user?.last_name,
                      ]
                        .filter(
                          Boolean
                        )
                        .join(" ") ||
                      user?.username ||
                      "You"
                    ).trim()}
                  />
                </div>
              </div>
            </aside>
          )}
        </div>

        <div className="app-footer-pad" />
      </main>

      {/* Desktop Upload Modal */}
      {!(onIOS || isMobile) &&
        uploadOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() =>
                setUploadOpen(false)
              }
            />

            <div
              className="
                relative
                z-10
                flex
                w-full
                max-w-xl
                flex-col
                max-h-[90dvh]
                overflow-hidden
                rounded-2xl
                bg-slate-50
                shadow-xl
              "
            >
              <div className="flex shrink-0 items-center justify-between px-5 pt-4 pb-2">
                <h3 className="font-black tracking-[-0.01em] text-blue-600 text-[18px]">
                  Upload Activity
                </h3>

                <button
                  type="button"
                  onClick={() =>
                    setUploadOpen(
                      false
                    )
                  }
                  className="
                    grid
                    !min-h-0
                    !min-w-0
                    h-8
                    w-8
                    place-items-center
                    text-gray-500
                  "
                  aria-label="Close"
                >
                  <X
                    size={18}
                    weight="bold"
                  />
                </button>
              </div>

              <div className="min-h-0 max-h-[calc(90dvh-56px)] overflow-y-auto">
                <ActivityUpload
                  fetchFeed={async () => {
                    await handleUploadSuccess();
                  }}
                  onClose={() =>
                    setUploadOpen(
                      false
                    )
                  }
                />
              </div>
            </div>
          </div>
        )}

      {/* Planner modals */}
      <MonthlyCalendarModal
        open={showMonthly}
        onClose={() =>
          setShowMonthly(false)
        }
        activitiesByDay={
          myActivitiesByDay
        }
        currentUser={user}
        streakDays={streakDays}
      />

      <WeeklyCalendarModal
        open={showWeekly}
        onClose={() =>
          setShowWeekly(false)
        }
        activitiesByDay={
          myActivitiesByDay
        }
        currentUser={user}
      />

      <WeeklyPlanModal
        open={showWeekPlan}
        onClose={() =>
          setShowWeekPlan(false)
        }
        keySuffix="tagfeed"
      />

      <GoalsByMonth
        open={showGoalsByMonth}
        onClose={() =>
          setShowGoalsByMonth(
            false
          )
        }
        keySuffix="tagfeed"
      />

      <RemindersModal
        open={showReminders}
        onClose={() =>
          setShowReminders(
            false
          )
        }
        storageKey="tagfeed:reminders"
      />
    </div>
  );
}